import { Link } from 'react-router-dom';

import { DocPage } from '@/components/doc-page';

export default function TermsPage() {
  return (
    <DocPage
      kicker="Terms of service"
      title="Terms of Service"
      lead="These terms govern your use of Bramble and our website."
      updated="21 July 2026"
    >
      <h2>1. Acceptance</h2>
      <p>
        By downloading or using Bramble (the &ldquo;app&rdquo;) or this website, you agree to these
        terms. If you do not agree, do not use them. Since there are no user accounts, you terminate
        this agreement by deleting the app.
      </p>

      <h2>2. Bramble is not medical advice</h2>
      <p>
        <strong>
          Bramble is a personal record-keeping tool. It is not a medical device, and nothing in it
          is medical advice, diagnosis, or treatment.
        </strong>{' '}
        Growth logs, charts, feeding totals, sleep summaries, and anything you record about
        illnesses or medicines are calculated from, or simply repeat back, what you entered. They are
        not evaluated against WHO or CDC growth curves, are not reviewed by a clinician, and carry no
        clinical meaning on their own.
      </p>
      <ul>
        <li>
          Always consult a pediatrician or qualified health professional about your child&rsquo;s
          feeding, weight, growth, sleep, or health.
        </li>
        <li>
          Dosage schedules and dose reminders repeat back the interval you entered. They are not a
          dosing recommendation, are not checked against a prescription or your child&rsquo;s weight,
          and can be missed or delayed by the operating system. Follow the instructions on the
          medicine and from your pediatrician, and do not rely on Bramble to tell you what or when
          to give.
        </li>
        <li>
          Never delay seeking medical care, or disregard advice you have been given, because of
          information in this app.
        </li>
        <li>
          If you believe your child has a medical emergency, call your local emergency number
          immediately. Do not use the app or the support form.
        </li>
        <li>
          Do not rely on Bramble as the sole record for a clinical decision. Data can be lost with a
          lost phone, and sync can be delayed (see section 4).
        </li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use Bramble or its sync relay for anything other than logging and syncing your own
          family&rsquo;s child-care records.
        </li>
        <li>
          Use the relay as general-purpose file storage, a message bus, or a transport for unrelated
          data.
        </li>
        <li>
          Attempt to overload, flood, disrupt, or gain unauthorized access to the relay, this
          website, or another channel, or attempt to analyze traffic patterns on the relay.
        </li>
        <li>
          Reverse engineer the service to circumvent rate limits or automate support form
          submissions.
        </li>
        <li>
          Use Bramble to record data about a child without legal right or parental authority.
        </li>
        <li>Use the service in violation of applicable laws.</li>
      </ul>
      <p>
        We reserve the right to block channels or IP addresses violating these terms.
      </p>

      <h2>4. Availability, sync, and data</h2>
      <p>
        Bramble is designed to work offline. The sync relay is provided without uptime guarantees.
        Sync message delivery is <strong>not guaranteed or instant</strong>: delivery relies on best-effort
        push notifications, and undelivered messages are discarded after 90 days.
      </p>
      <p>
        <strong>You are responsible for your own backups.</strong> Your records remain on your
        device. We hold no readable copy and cannot restore data after loss or deletion. See the{' '}
        <Link to="/privacy">privacy policy</Link>.
      </p>
      <p>
        We may modify, suspend, or discontinue any part of the service at any time. We will provide
        notice in app release notes when practical.
      </p>

      <h2>5. Your content</h2>
      <p>
        You retain all rights to your data. We claim no license over it and cannot access encrypted
        content. You are responsible for the content you record.
      </p>

      <h2>6. Disclaimer of warranties</h2>
      <p>
        Bramble is provided <strong>&ldquo;as is&rdquo; and &ldquo;as available&rdquo;</strong>,
        without warranties of any kind, express or implied. We do not warrant that the app will be
        uninterrupted or error-free, or that data will sync, arrive in order, or be preserved.
      </p>

      <h2>7. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, Bramble and its creators are not liable for any
        indirect, incidental, special, consequential, or punitive damages, or loss of data or health
        outcomes, arising from your use of the app or relay, even if advised of the possibility.
      </p>
      <p>
        Because Bramble is provided free of charge, total aggregate liability for all claims
        relating to the service is limited to the amount paid (zero) or USD 50, whichever is greater.
      </p>

      <h2>8. Changes to these terms</h2>
      <p>
        We may update these terms. Updated dates will appear at the top of this page, and material
        changes will be noted in release notes. Continued use of Bramble constitutes acceptance of
        the updated terms.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions regarding these terms can be sent via the <Link to="/support">support page</Link>.
      </p>
    </DocPage>
  );
}
